import type {
  FormField,
  Rank,
  RetirementFormData,
  RetirementStorageData,
  Sections,
} from 'data/retirementPlanner/retirementPlannerTypes';
import { v4 as uuidv4 } from 'uuid';
export const plannerFormWidth = 370;

export const hasErrors = (errors: any) => {
  return Object.values(errors).some((error) => error !== '');
};

export type Errors = { [key: string]: string };

export const formFields: FormField[] = [
  { section: 'CURRENT', id: 'startingAge', rank: 'BOTH', default: '40' },
  { section: 'CURRENT', id: 'startingBalanceCombined', rank: 'BASIC', default: '150000' },
  { section: 'CURRENT', id: 'startingBalanceTaxDeferred', rank: 'ADVANCED', default: '100000' },
  { section: 'CURRENT', id: 'startingBalanceTaxDeferred', rank: 'BASICHIDDEN', default: '0' },
  { section: 'CURRENT', id: 'startingBalanceTaxable', rank: 'ADVANCED', default: '50000' },

  { section: 'CONTRIBUTIONS', id: 'annualContributionTaxDeferred', rank: 'ADVANCED', default: '10000' },
  { section: 'CONTRIBUTIONS', id: 'annualContributionTaxDeferred', rank: 'BASICHIDDEN', default: '0' },
  { section: 'CONTRIBUTIONS', id: 'annualContributionTaxable', rank: 'ADVANCED', default: '5000' },
  { section: 'CONTRIBUTIONS', id: 'annualContributionCombined', rank: 'BASIC', default: '15000' },
  { section: 'CONTRIBUTIONS', id: 'annualContributionIncrease', rank: 'ADVANCED', default: '3' },
  { section: 'CONTRIBUTIONS', id: 'annualContributionIncrease', rank: 'BASICHIDDEN', default: '0' },

  { section: 'RETIREMENT', id: 'retirementAge', rank: 'BOTH', default: '64' },
  { section: 'RETIREMENT', id: 'lifeExpectancy', rank: 'BOTH', default: '78' },
  { section: 'RETIREMENT', id: 'annualWithdrawal', rank: 'BOTH', default: '50000' },
  { section: 'RETIREMENT', id: 'retirementIncome', rank: 'BOTH', default: '40000' },
  { section: 'RETIREMENT', id: 'inflationRate', rank: 'ADVANCED', default: '3' },
  { section: 'RETIREMENT', id: 'inflationRate', rank: 'BASICHIDDEN', default: '3' },

  { section: 'RETURN', id: 'meanReturn', rank: 'BOTH', default: '7' },
  { section: 'RETURN', id: 'meanReturnPostRetirement', rank: 'ADVANCED', default: '3' },
  { section: 'RETURN', id: 'meanReturnPostRetirement', rank: 'BASICHIDDEN', default: '7' },
  { section: 'RETURN', id: 'preRetirementTaxRate', rank: 'BOTH', default: '22' },
  { section: 'RETURN', id: 'taxRate', rank: 'BOTH', default: '22' },
];

export const getRank = (isAdvanced: boolean): Rank => (isAdvanced ? 'ADVANCED' : 'BASIC');

export const sectionFields: { [key in Sections]: (keyof RetirementFormData)[] } = {
  CURRENT: formFields.filter((field) => field.section === 'CURRENT').map((field) => field.id),
  CONTRIBUTIONS: formFields.filter((field) => field.section === 'CONTRIBUTIONS').map((field) => field.id),
  RETIREMENT: formFields.filter((field) => field.section === 'RETIREMENT').map((field) => field.id),
  RETURN: formFields.filter((field) => field.section === 'RETURN').map((field) => field.id),
};

export const getFieldsBySectionAndRank = (section: Sections, rank: Rank): FormField[] =>
  formFields.filter(
    (field) => section === field.section && (rank === 'BOTH' || field.rank === rank || field.rank === 'BOTH'),
  );

export const getFieldsByRankSpecific = (rank: Rank): FormField[] => formFields.filter((field) => field.rank === rank);

export const getFieldIdsByRankSpecific = (rank: Rank): (keyof RetirementFormData)[] =>
  getFieldsByRankSpecific(rank).map((field) => field.id);

export const getFieldById = (fieldName: keyof RetirementFormData): FormField | undefined =>
  formFields.find((field) => field.id === fieldName);
export const getFieldRank = (fieldName: keyof RetirementFormData): Rank | null => getFieldById(fieldName)?.rank ?? null;

export const getFieldIdsByRank = (rank: Rank): (keyof RetirementFormData)[] => {
  const ids = [] as (keyof RetirementFormData)[];
  formFields.forEach((field) => {
    if (field.rank === rank || field.rank === 'BOTH') {
      ids.push(field.id);
    }
  });
  return ids;
};

export const getDefaultsByRank = (rank: Rank): RetirementFormData => {
  const defaults: RetirementFormData = {} as RetirementFormData;
  formFields.forEach((field) => {
    if (field.rank === rank || field.rank === 'BOTH') {
      defaults[field.id] = String(field.default);
    }
  });
  return defaults;
};

/*
 * AdjustFormDataForMode
 * Adjust the formData based on the input mode (simple or advanced)
 */
export const adjustFormDataForMode = (formData: RetirementFormData, isAdvanced: boolean) => {
  if (!isAdvanced) {
    const defaultFormData = getDefaultsByRank('BASICHIDDEN'); // Get advanced default form data
    // SIMPLE MODE
    const updatedFormData = {
      ...formData,
      startingBalanceTaxable: formData.startingBalanceCombined,
      startingBalanceTaxDeferred: defaultFormData.startingBalanceTaxDeferred,
      annualContributionTaxable: formData.annualContributionCombined,
      annualContributionTaxDeferred: defaultFormData.annualContributionTaxDeferred,
      annualContributionIncrease: defaultFormData.annualContributionIncrease,
      inflationRate: defaultFormData.inflationRate,
      meanReturnPostRetirement: formData.meanReturn,
    };
    return updatedFormData;
  }
  return formData;
};

export const validateFields = (formData: RetirementFormData, isAdvanced: boolean) => {
  const errors: Errors = {};

  const startingAge = Number(formData.startingAge);
  const retirementAge = Number(formData.retirementAge);
  const lifeExpectancy = Number(formData.lifeExpectancy);

  // determine fields to use
  const names = getFieldIdsByRank(getRank(isAdvanced));
  names.forEach((name) => {
    let error = '';
    const value = Number(formData[name]);

    switch (name) {
      case 'annualWithdrawal':
        if (value <= 0) {
          error = 'Annual withdrawal must be a positive number.';
        }
        break;

      case 'lifeExpectancy':
        if (value < startingAge) {
          error = 'Life expectancy must be greater than or equal to your current age.';
        } else if (value < retirementAge) {
          error = 'Life expectancy must be greater than or equal to retirement age.';
        } else if (value > 120) {
          // Added upper limit for life expectancy
          error = 'Life expectancy must be less than or equal to 120 years.';
        }
        break;

      case 'startingAge':
        if (value > lifeExpectancy) {
          error = 'Starting age must be less than your life expectancy';
        } else if (value > retirementAge) {
          error = 'Starting age must be less than or equal to retirement age.';
        }
        break;

      case 'retirementAge':
        if (value < startingAge) {
          error = 'Retirement age must be greater than or equal to your current age.';
        } else if (value > lifeExpectancy) {
          error = 'Retirement age must be less than or equal to life expectancy.';
        }
        break;

      case 'meanReturn':
      case 'meanReturnPostRetirement':
        if (value < -50 || value > 50) {
          error = 'Expected return should be between -50% and 50%.';
        }
        break;

      case 'preRetirementTaxRate':
      case 'taxRate':
        if (value < 0 || value > 100) {
          error = 'Tax rate must be between 0% and 100%.';
        }
        break;

      case 'inflationRate':
        if (value < 0 || value > 20) {
          error = 'Inflation rate must be between 0% and 20%.';
        }
        break;

      case 'retirementIncome':
        if (value < 0) {
          error = 'Retirement income cannot be negative.';
        }
        break;

      case 'startingBalanceCombined':
      case 'startingBalanceTaxable':
      case 'startingBalanceTaxDeferred':
      case 'annualContributionCombined':
      case 'annualContributionTaxable':
      case 'annualContributionTaxDeferred':
        if (value < 0) {
          error = 'Value cannot be negative.';
        }
        break;

      case 'annualContributionIncrease':
        if (value < 0 || value > 50) {
          error = 'Annual contribution increase must be between 0% and 50%.';
        }
        break;

      default:
        break;
    }

    errors[name] = error;
  });

  return errors;
};

/**
 * Converts every property value of `someObject` into a string.
 *
 * @param someObject - An object whose property values will be converted.
 * @returns A new object with the same keys, but all values are strings.
 */
export function convertAllToString<T extends object>(someObject: T): { [K in keyof T]: string } {
  if (!someObject) return someObject as { [K in keyof T]: string };
  return Object.keys(someObject).reduce(
    (acc, key) => {
      acc[key as keyof T] = String((someObject as any)[key]);
      return acc;
    },
    {} as { [K in keyof T]: string },
  );
}
export const retirementStorageDataDefault: RetirementStorageData = {
  id: undefined,
  clientId: uuidv4().toUpperCase(),
  createdAt: new Date().toISOString(),
  modifiedAt: new Date().toISOString(),
  isDeleted: false,
  data: {
    formDataBasic: getDefaultsByRank('BASIC'),
    formDataAdvanced: getDefaultsByRank('ADVANCED'),
    isAdvanced: false,
    showInflationAdjusted: false,
    introVisible: true,
  },
};
